// AI_GOOD: Service classes extracted for better separation of concerns
import axios from "axios";
import { logger } from "@/utils/logger";
import { 
  MissingArticleItemSchema,
  UploadedFile,
  TranslateRequestSchema as FrontendTranslateRequestSchema
} from "@/types/missing_articles";
import { ANIMATION_CONFIG } from "@/pages/MissingArticle/types";

// AI_GOOD: Generation service class
export class GenerationService {
  constructor(
    private apiBaseUrl: string,
    private authToken: string
  ) {}

  // AI_GOOD: Streaming generation with improved throttling logic
  async streamGenerate(
    itemId: string,
    selectedGenLanguage: string,
    currentUserPrompt: string,
    uploadedFiles: UploadedFile[],
    enableUrlContext: boolean,
    enableGoogleSearch: boolean,
    onContentChunk: (text: string) => void,
    onThoughtChunk: (text: string) => void,
    onError: (error: string) => void,
    onSuccess: (message: string, version?: number) => void,
    onWarning: (message: string, version?: number) => void
  ): Promise<void> {
    const formData = new FormData();
    formData.append("target_language", selectedGenLanguage);
    if (currentUserPrompt) {
      formData.append("user_prompt_addition", currentUserPrompt);
    }
    uploadedFiles.forEach((upFile) => formData.append("files", upFile.file));
    formData.append("enable_url_context", String(enableUrlContext));
    formData.append("enable_google_search", String(enableGoogleSearch));

    // AI_FIXED: Add debugging counters
    let totalChunks = 0;
    let contentChunks = 0;
    let thoughtChunks = 0;
    let errorChunks = 0;
    const startTime = performance.now();

    console.log(`🚀 Starting stream generation for item ${itemId}, language: ${selectedGenLanguage}`);

    try {
      const response = await fetch(
        `${this.apiBaseUrl}/missing-articles/item/${itemId}/stream-generate-draft`,
        {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${this.authToken}` },
        },
      );

      if (!response.ok || !response.body) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || `Request failed: ${response.status}`,
        );
      }

      console.log(`✅ HTTP Response received. Status: ${response.status}`);
      console.log(`📋 Response headers:`, Object.fromEntries(response.headers.entries()));

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let newVersionNumber: number | undefined = undefined;
      let localStreamWarning: string | undefined = undefined;
      
      // AI_FIXED: Improved animation tracking without artificial delays
      let contentLastUpdateTime = 0;
      let thoughtsLastUpdateTime = 0;
      let pendingContentUpdate: string | null = null;
      let pendingThoughtsUpdate: string | null = null;
      
      // AI_FIXED: Use requestAnimationFrame for smooth updates instead of setTimeout
      const scheduleContentUpdate = (text: string) => {
        const now = performance.now();
        if (now - contentLastUpdateTime >= ANIMATION_CONFIG.CONTENT_UPDATE_INTERVAL) {
          onContentChunk(text);
          contentLastUpdateTime = now;
        } else {
          // Instead of setTimeout, accumulate and use requestAnimationFrame
          pendingContentUpdate = (pendingContentUpdate || "") + text;
          if (pendingContentUpdate === text) { // Only schedule if this is the first pending update
            requestAnimationFrame(() => {
              if (pendingContentUpdate) {
                onContentChunk(pendingContentUpdate);
                pendingContentUpdate = null;
                contentLastUpdateTime = performance.now();
              }
            });
          }
        }
      };

      const scheduleThoughtsUpdate = (text: string) => {
        const now = performance.now();
        if (now - thoughtsLastUpdateTime >= ANIMATION_CONFIG.THOUGHTS_UPDATE_INTERVAL) {
          onThoughtChunk(text);
          thoughtsLastUpdateTime = now;
        } else {
          // Accumulate thoughts and use requestAnimationFrame
          pendingThoughtsUpdate = (pendingThoughtsUpdate || "") + text;
          if (pendingThoughtsUpdate === text) { // Only schedule if this is the first pending update
            requestAnimationFrame(() => {
              if (pendingThoughtsUpdate) {
                onThoughtChunk(pendingThoughtsUpdate);
                pendingThoughtsUpdate = null;
                thoughtsLastUpdateTime = performance.now();
              }
            });
          }
        }
      };

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunkText = decoder.decode(value, { stream: true });
          totalChunks++;
          
          // AI_GOOD: Process each line separately as it might contain multiple JSON objects
          const lines = chunkText.split('\n');
          
          for (const line of lines) {
            if (!line.trim()) continue;
            
            try {
              // AI_GOOD: Try to parse as JSON
              const jsonData = JSON.parse(line.trim());
              
              // AI_GOOD: Handle different message types
              switch (jsonData.type) {
                case 'content':
                  contentChunks++;
                  console.log(`📝 Content chunk #${contentChunks} (${jsonData.text.length} chars):`, jsonData.text.substring(0, 50) + '...');
                  // AI_FIXED: Use improved scheduling without setTimeout delays
                  scheduleContentUpdate(jsonData.text);
                  break;
                
                case 'thought':
                  thoughtChunks++;
                  console.log(`💭 Thought chunk #${thoughtChunks} (${jsonData.text.length} chars):`, jsonData.text.substring(0, 50) + '...');
                  // AI_FIXED: Use improved scheduling for thoughts
                  scheduleThoughtsUpdate(jsonData.text);
                  break;
                
                case 'error':
                  errorChunks++;
                  console.error(`❌ Error chunk #${errorChunks}:`, jsonData.text);
                  onError(jsonData.text || "An error occurred during generation.");
                  break;
                
                case 'success':
                  const versionMatch = jsonData.text.match(/Version (\d+)/);
                  if (versionMatch && versionMatch[1]) {
                    newVersionNumber = parseInt(versionMatch[1], 10);
                  }
                  console.log(`✅ Success:`, jsonData.text);
                  onSuccess(jsonData.text, newVersionNumber);
                  break;
                  
                case 'success_warning':
                  const warningVersionMatch = jsonData.text.match(/Version (\d+)/);
                  if (warningVersionMatch && warningVersionMatch[1]) {
                    newVersionNumber = parseInt(warningVersionMatch[1], 10);
                    const warningMatch = jsonData.text.match(/\(Warning: (.*?)\)/);
                    if (warningMatch && warningMatch[1]) {
                      localStreamWarning = warningMatch[1];
                    }
                  }
                  console.log(`⚠️ Success with warning:`, jsonData.text);
                  onWarning(jsonData.text, newVersionNumber);
                  break;
                  
                default:
                  // AI_GOOD: Unknown message type, log it
                  console.log("❓ Unknown message type:", jsonData);
                  break;
              }
            } catch (e) {
              // AI_GOOD: Not valid JSON, handle as plain text (backward compatibility)
              console.log("📄 Non-JSON chunk:", line);
              
              // AI_GOOD: Legacy handling for any non-JSON responses
              if (line.includes("STREAM_ERROR:")) {
                onError(
                  line.split("STREAM_ERROR:")[1]?.trim() ||
                    "An error occurred during generation.",
                );
              } else if (line.includes("STREAM_SUCCESS:")) {
                const successMatch = line.match(
                  /STREAM_SUCCESS: Saved as Version (\d+)(?: \((.*)\))?/,
                );
                if (successMatch && successMatch[1]) {
                  newVersionNumber = parseInt(successMatch[1], 10);
                  const fullSuccessMessage = `Saved as Version ${newVersionNumber}`;
                  if (successMatch[2]) {
                    localStreamWarning = successMatch[2];
                    onWarning(`${fullSuccessMessage} (${localStreamWarning})`, newVersionNumber);
                  } else {
                    onSuccess(fullSuccessMessage, newVersionNumber);
                  }
                }
              } else {
                // AI_GOOD: Do not automatically add plain text to content stream
                console.log("🤷 Ignoring non-structured content:", line);
              }
            }
          }
        }
      }

      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000;
      
      console.log(`🏁 Stream completed in ${duration.toFixed(2)}s`);
      console.log(`📊 Stream statistics: Total chunks: ${totalChunks}, Content: ${contentChunks}, Thoughts: ${thoughtChunks}, Errors: ${errorChunks}`);
      
    } catch (err) {
      console.error("❌ Error generating draft:", err);
      const errorMsg = err instanceof Error ? err.message : "An unknown error occurred.";
      onError(errorMsg);
    }
  }

  // AI_GOOD: Non-streaming generation preserved exactly
  async generateNonStreaming(
    itemId: string,
    selectedGenLanguage: string,
    currentUserPrompt: string,
    uploadedFiles: UploadedFile[],
    enableUrlContext: boolean,
    enableGoogleSearch: boolean
  ): Promise<MissingArticleItemSchema> {
    const formData = new FormData();
    formData.append("target_language", selectedGenLanguage);
    if (currentUserPrompt) {
      formData.append("user_prompt_addition", currentUserPrompt);
    }
    uploadedFiles.forEach((upFile) => formData.append("files", upFile.file));
    formData.append("enable_url_context", String(enableUrlContext));
    formData.append("enable_google_search", String(enableGoogleSearch));

    const response = await axios.post<MissingArticleItemSchema>(
      `${this.apiBaseUrl}/missing-articles/item/${itemId}/generate-draft-nostream`,
      formData,
      { headers: { Authorization: `Bearer ${this.authToken}` } },
    );

    return response.data;
  }
}

// AI_GOOD: Translation service class
export class TranslationService {
  constructor(
    private apiBaseUrl: string,
    private authToken: string
  ) {}

  // AI_GOOD: Streaming translation request
  async translateContent(
    itemId: string,
    version: number,
    fromLanguage: string,
    toLanguage: string,
    contentToTranslate: string,
    enableUrlContext: boolean = false,
    enableGoogleSearch: boolean = false,
    onContentChunk: (text: string) => void,
    onThoughtChunk: (text: string) => void,
    onError: (error: string) => void,
    onSuccess: (message: string, version?: number) => void,
    onInfo: (message: string) => void
  ): Promise<void> {
    const payload: FrontendTranslateRequestSchema = {
      version,
      from_language: fromLanguage,
      to_language: toLanguage,
      content: contentToTranslate,
      enable_url_context: enableUrlContext,
      enable_google_search: enableGoogleSearch,
    };

    logger.info(`🚀 Starting stream translation for item ${itemId}, version: ${version}, from: ${fromLanguage}, to: ${toLanguage}`);
    onInfo(`Starting translation to ${toLanguage.toUpperCase()}...`);

    try {
      const response = await fetch(
        `${this.apiBaseUrl}/missing-articles/item/${itemId}/translate`,
        {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            Authorization: `Bearer ${this.authToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok || !response.body) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail || `Request failed: ${response.status}`,
        );
      }

      logger.info(`✅ HTTP Response received. Status: ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunkText = decoder.decode(value, { stream: true });
          const lines = chunkText.split('\n');

          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const jsonData = JSON.parse(line.trim());

              switch (jsonData.type) {
                case 'content':
                  onContentChunk(jsonData.text);
                  break;
                case 'thought':
                  onThoughtChunk(jsonData.text);
                  break;
                case 'error':
                  onError(jsonData.text);
                  break;
                case 'success':
                  const versionMatch = jsonData.text.match(/Version (\d+)/);
                  const completedVersion = versionMatch ? parseInt(versionMatch[1], 10) : version;
                  onSuccess(jsonData.text, completedVersion);
                  break;
                case 'info':
                  onInfo(jsonData.text);
                  break;
                default:
                  logger.warn("Unknown stream message type:", jsonData);
                  break;
              }
            } catch (e) {
              logger.error("Error parsing stream chunk. Raw line:", { error: e, rawLine: line });
            }
          }
        }
      }

      logger.info(`🏁 Stream translation completed for item ${itemId}`);

    } catch (err) {
      logger.error("❌ Error during stream translation:", err);
      const errorMsg = err instanceof Error ? err.message : "An unknown error occurred.";
      onError(errorMsg);
    }
  }
} 